import React, { useRef, useEffect, useCallback } from 'react';
import { Camera } from '../rendering/camera';
import { GameEngine } from '../game/state/gameEngine';
import { renderBoard, RenderTheme } from '../rendering/renderer';
import { cellToBox } from '../game/core/coordinates';

interface BoardCanvasProps {
  engine: GameEngine;
  camera: Camera;
  theme: RenderTheme;
  flagMode: boolean;
  onRevealCell: (x: number, y: number) => void;
  onToggleFlag: (x: number, y: number) => void;
  onSelectBustedBox: (boxX: number, boxY: number) => void;
  onCameraChange: () => void;
}

export const BoardCanvas: React.FC<BoardCanvasProps> = ({
  engine,
  camera,
  theme,
  flagMode,
  onRevealCell,
  onToggleFlag,
  onSelectBustedBox,
  onCameraChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Gesture tracking state
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const cameraStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const touchDistanceRef = useRef<number | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasMovedRef = useRef(false);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.parentElement?.clientWidth || window.innerWidth;
    const height = canvas.parentElement?.clientHeight || window.innerHeight;

    renderBoard(ctx, width, height, camera, engine, theme);
  }, [camera, engine, theme]);

  // RequestAnimationFrame redraw loop
  useEffect(() => {
    let animationFrameId: number;

    const loop = () => {
      draw();
      animationFrameId = requestAnimationFrame(loop);
    };
    loop();

    return () => cancelAnimationFrame(animationFrameId);
  }, [draw]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => draw();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [draw]);

  // Attach non-passive wheel and touch listeners to allow e.preventDefault()
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheelNative = (e: WheelEvent) => {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      camera.setZoom(camera.zoom * zoomFactor);
      onCameraChange();
    };

    const handleTouchMoveNative = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault(); // Prevent default browser pinch zoom
      }
    };

    canvas.addEventListener('wheel', handleWheelNative, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMoveNative, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', handleWheelNative);
      canvas.removeEventListener('touchmove', handleTouchMoveNative);
    };
  }, [camera, onCameraChange]);

  // Mouse / Touch Event Handlers
  const handlePointerDown = (clientX: number, clientY: number) => {
    isDraggingRef.current = true;
    hasMovedRef.current = false;
    dragStartRef.current = { x: clientX, y: clientY };
    cameraStartRef.current = { x: camera.x, y: camera.y };

    // Setup Long Press timer (350ms) for flagging
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      if (!hasMovedRef.current && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const screenX = clientX - rect.left;
        const screenY = clientY - rect.top;
        const width = rect.width;
        const height = rect.height;

        const { x, y } = camera.screenToWorld(screenX, screenY, width, height);
        const { boxX, boxY } = cellToBox(x, y);

        if (engine.getBoxStatus(boxX, boxY) === 'ACTIVE') {
          onToggleFlag(x, y);
          hasMovedRef.current = true; // Prevent tap action
        }
      }
    }, 350);
  };

  const handlePointerMove = (clientX: number, clientY: number) => {
    if (!isDraggingRef.current) return;

    const dx = clientX - dragStartRef.current.x;
    const dy = clientY - dragStartRef.current.y;

    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      hasMovedRef.current = true;
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }

    if (hasMovedRef.current) {
      // Update camera position based on drag delta
      camera.x = cameraStartRef.current.x - dx / camera.zoom;
      camera.y = cameraStartRef.current.y - dy / camera.zoom;
      onCameraChange();
    }
  };

  const handlePointerUp = (clientX: number, clientY: number) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (!hasMovedRef.current && canvasRef.current) {
      // Tap action
      const rect = canvasRef.current.getBoundingClientRect();
      const screenX = clientX - rect.left;
      const screenY = clientY - rect.top;
      const width = rect.width;
      const height = rect.height;

      const { x, y } = camera.screenToWorld(screenX, screenY, width, height);
      const { boxX, boxY } = cellToBox(x, y);
      const status = engine.getBoxStatus(boxX, boxY);

      if (status === 'BUSTED') {
        onSelectBustedBox(boxX, boxY);
      } else if (status === 'ACTIVE') {
        if (flagMode) {
          onToggleFlag(x, y);
        } else {
          onRevealCell(x, y);
        }
      }
    }

    isDraggingRef.current = false;
    touchDistanceRef.current = null;
  };

  // Touch Gesture Listeners (Pinch Zoom & Drag)
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 1) {
      handlePointerDown(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      // Pinch start
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      hasMovedRef.current = true;
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      touchDistanceRef.current = dist;
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 1) {
      handlePointerMove(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2 && touchDistanceRef.current !== null) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const factor = dist / touchDistanceRef.current;
      camera.setZoom(camera.zoom * factor);
      touchDistanceRef.current = dist;
      onCameraChange();
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    if (e.touches.length === 0) {
      const touch = e.changedTouches[0];
      handlePointerUp(touch.clientX, touch.clientY);
    }
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none' }}
        onMouseDown={(e) => handlePointerDown(e.clientX, e.clientY)}
        onMouseMove={(e) => handlePointerMove(e.clientX, e.clientY)}
        onMouseUp={(e) => handlePointerUp(e.clientX, e.clientY)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
    </div>
  );
};
